import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { getInitials, getICPConfig, getProxiedImageUrl } from '../../../utils/helpers';
import { getStatusConfig, getNormalizedStatusKey, getTransitionalStatusLabel, getLeadSubstatusDisplay } from '../../../utils/statusMapping';
import { ChannelIcons } from '../ChannelIcons';

export interface ViewCardProps {
  id: string;
  profilePicture?: string;
  profilePictureStorageUrl?: string | null;
  companyLogo?: string;
  name: string;
  title: string;
  company: string;
  industry?: string;
  location: string;
  status?: string;
  /** DB lead_substatus — shown under the umbrella status when present. */
  salesSubstatus?: string | null;
  icpScore: number;
  replaces?: string | null;
  replacementLabel?: string | null;
  replacementDetailLabel?: string | null;
  replacementCompanyLabel?: string | null;
  replacementReason?: string | null;
  isSelected?: boolean;
  onClick?: () => void;
  onOpenISR?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onToggleSelect?: (id: string) => void;
  reportDownloadUrl?: string | null;
  recoveryTitle?: string | null;
  recoveryDescription?: string | null;
  recoveryActionLabel?: string | null;
  recoveryLoading?: boolean;
  onRecoverClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  authToken?: string | null;
  channels?: Record<string, unknown>;
  /** Email verification data — colors the email channel icon by quality and
   *  shows the verification card on hover. Passed straight to ChannelIcons. */
  emailProviderData?: unknown;
}

export const ViewCard: React.FC<ViewCardProps> = ({
  id,
  profilePicture,
  profilePictureStorageUrl,
  companyLogo,
  name,
  title,
  company,
  industry,
  location,
  status,
  salesSubstatus,
  icpScore,
  replaces,
  replacementLabel,
  replacementDetailLabel,
  replacementCompanyLabel,
  replacementReason,
  isSelected = false,
  onClick,
  onOpenISR,
  onToggleSelect,
  reportDownloadUrl,
  recoveryTitle,
  recoveryDescription,
  recoveryActionLabel,
  recoveryLoading = false,
  onRecoverClick,
  authToken,
  channels,
  emailProviderData,
}) => {
  const [imageError, setImageError] = useState(false);
  const [companyLogoError, setCompanyLogoError] = useState(false);
  const icpConfig = getICPConfig(icpScore);
  const companyLogoUrl = companyLogo
    ? getProxiedImageUrl(companyLogo, undefined, authToken)
    : undefined;

  // Use centralized status config from utils/statusMapping.ts
  const statusConfig = getStatusConfig(status);
  const transitionalLabel = getTransitionalStatusLabel(status);
  // OUTREACHING with no substatus defaults to "Not started"; other statuses
  // show only what they carry.
  const substatusLabel = getLeadSubstatusDisplay(status, salesSubstatus);
  const statusStyle = {
    // Transitional substatus ("Writing message…" — now under ENRICHED) wins;
    // otherwise show the DB substatus when the lead carries one; otherwise the
    // umbrella status label.
    label: transitionalLabel ?? (substatusLabel ?? statusConfig.label),
    parentLabel: transitionalLabel || substatusLabel ? statusConfig.label : null,
    color: statusConfig.tailwindColor,
    bg: statusConfig.tailwindBg,
    border: statusConfig.tailwindBorder,
  };

  // Only show ICP score for enriched leads (not NEW LEAD or ENRICHING)
  const shouldShowICP = icpScore > 0 && !['NEW LEAD', 'ENRICHING'].includes(statusConfig.label);
  const hasReplacement = Boolean(replaces || replacementReason?.trim());
  const replacementHeading = replaces ? `Why This Lead Replaced #${replaces}` : 'Why This Lead Replaced Another Lead';
  const replacementName = replacementDetailLabel || replacementLabel || (replaces ? `Lead #${replaces}` : 'Lead marked as replacement during enrichment');

  return (
    <div
      onClick={onClick}
      className={`lead-view-card relative bg-[#0A0A0A] border rounded-2xl p-6 hover:border-white/20 transition-all duration-300 cursor-pointer group hover:shadow-xl hover:shadow-black/30 ${
        isSelected
          ? 'border-[#5260FF] ring-2 ring-[#5260FF]/30'
          : 'border-white/10'
      }`}
    >
      {hasReplacement && (
        <div
          className="group/replacement-logo absolute left-3 top-3 z-20 inline-flex"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            tabIndex={0}
            role="img"
            aria-label={`${replacementLabel || 'Replacement Lead'}: ${replacementName}`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-emerald-400/30 bg-black/75 text-emerald-300 shadow-[0_0_20px_rgba(16,185,129,0.16)] backdrop-blur-md transition-all hover:border-emerald-300/45 hover:bg-emerald-500/12 hover:text-emerald-100 focus:border-emerald-300/45 focus:bg-emerald-500/12 focus:text-emerald-100 focus:outline-none"
          >
            <Icon icon="bx:transfer-alt" className="h-4 w-4" aria-hidden="true" />
          </div>

          <div className="pointer-events-none absolute left-0 top-full z-30 mt-2 hidden w-[300px] rounded-2xl border border-emerald-500/20 bg-[#0B1110]/96 p-4 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-xl group-hover/replacement-logo:block group-focus-within/replacement-logo:block">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300">
                Replaced Lead
              </div>
              <div className="mt-1 truncate text-sm font-semibold text-white">{replacementName}</div>
              {replacementCompanyLabel && (
                <div className="mt-0.5 text-[11px] text-gray-400">{replacementCompanyLabel}</div>
              )}
            </div>

            <div className="mt-4 rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300">
                {replacementHeading}
              </div>
              <p className="mt-2 text-xs leading-5 text-gray-200">
                {replacementReason?.trim() || 'This lead was identified during enrichment as the stronger contact for this campaign.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Selection Checkbox - Top Right */}
      {onToggleSelect && (
        <div
          className="absolute top-3 right-3 z-20"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(id);
          }}
        >
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
            isSelected
              ? 'bg-[#5260FF] border-[#5260FF]'
              : 'border-white/30 hover:border-[#5260FF]/50 bg-black/60'
          }`}>
            {isSelected && (
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
              </svg>
            )}
          </div>
        </div>
      )}

      {/* ICP Score Badge with Glow - Only shown for enriched leads */}
      {shouldShowICP && (
        <div className={`absolute ${onToggleSelect ? 'top-12' : 'top-4'} right-4`}>
          <div
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${icpConfig.wrapperClass} shadow-lg flex flex-col items-center`}
            style={{
              boxShadow: `0 0 20px -5px ${icpConfig.glowColor || 'rgba(82, 96, 255, 0.4)'}`
            }}
          >
            <div className="text-[9px] uppercase tracking-wider opacity-70 leading-none mb-0.5">ICP</div>
            <div className="leading-none">{icpScore}%</div>
          </div>
        </div>
      )}

      {/* Profile Section — mt reserves the row under the absolutely positioned
          replacement badge (top-left) so the avatar never sits under it. */}
      <div className={`flex flex-col items-start mb-4 w-full ${hasReplacement ? 'mt-9' : ''}`}>
        {/* Profile Picture + Company Logo */}
        <div className="flex items-start gap-2 mb-3">
          <div className="lead-view-card__avatar w-16 h-16 rounded-full overflow-hidden border border-white/5 group-hover:border-[#5260FF]/50 transition-colors">
            {profilePicture && !imageError ? (
              <img
                src={getProxiedImageUrl(profilePicture, profilePictureStorageUrl, authToken)}
                alt={name}
                className="w-full h-full object-cover grayscale"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-[#0A0A0A] text-gray-600 text-lg font-medium">
                {getInitials(name)}
              </div>
            )}
          </div>

          {companyLogoUrl && !companyLogoError && (
            <div className="w-10 h-10 rounded-full overflow-hidden shadow-sm bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
              <img
                src={companyLogoUrl}
                alt={company}
                className="w-full h-full object-cover"
                onError={() => setCompanyLogoError(true)}
              />
            </div>
          )}

        </div>

        {/* Company Name - Second */}
        <div className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-1 w-full truncate">
          {company}
        </div>

        {/* Location - Below company */}
        {location && (
          <div className="flex items-center gap-1.5 text-gray-500 text-[11px] mb-2 w-full">
            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="truncate">{location}</span>
          </div>
        )}

        {/* Person Name - Third */}
        <h3 className="text-white font-bold text-base mb-1 group-hover:text-[#5260FF] transition-colors w-full">
          {name}
        </h3>

        {/* Title/Role - Fourth */}
        <p className="text-gray-400 text-sm mb-2 line-clamp-2 w-full">
          {title}
        </p>

        {/* Channel Icons */}
        {channels && (
          <ChannelIcons lead={channels} size="sm" className="mt-1" emailProviderData={emailProviderData} />
        )}
      </div>

      {/* Footer - Industry/CNAE & Status */}
      <div className="flex items-start justify-between gap-3 pt-4 text-xs">
        {/* Industry/CNAE - Split by comma for multiple codes */}
        {industry ? (
          <div className="flex-1 min-w-0 flex flex-col gap-0.5" title={industry}>
            {industry.split(',').map((cnae, index) => (
              <div
                key={index}
                className="text-[#5260FF] text-[11px] font-medium truncate"
              >
                {cnae.trim()}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex-1" />
        )}

        {/* Status Badge - Fixed width to prevent overlap. The umbrella status
            remains primary; an optional substatus reads as supporting progress. */}
        <div className={`inline-flex flex-shrink-0 cursor-pointer flex-col px-2.5 py-1 ${statusStyle.bg} ${statusStyle.border} border rounded-md self-start`}>
          {statusStyle.parentLabel && (
            <span className={`whitespace-nowrap text-[10px] font-black uppercase leading-none tracking-[0.08em] ${statusStyle.color}`}>{statusStyle.parentLabel}</span>
          )}
          <span className={`whitespace-nowrap uppercase ${statusStyle.color} ${statusStyle.parentLabel ? 'mt-0.5 text-[8px] font-semibold leading-none tracking-[0.08em] opacity-60' : 'text-[10px] font-black leading-none tracking-[0.08em]'}`}>{statusStyle.label}</span>
        </div>
      </div>
      {recoveryTitle && recoveryActionLabel && onRecoverClick && (
        <div className="mt-3 flex items-center gap-1.5">
          {getNormalizedStatusKey(status) !== 'enriching' && (
            <span
              className="relative inline-flex h-9 w-9 items-center justify-center rounded-xl border border-amber-400/20 bg-amber-400/8 text-amber-300/60"
              title={`Report missing${recoveryDescription ? ': ' + recoveryDescription : ''}`}
            >
              <Icon icon="solar:download-linear" className="h-4.5 w-4.5" aria-hidden="true" />
              <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400">
                <Icon icon="solar:danger-triangle-bold" className="h-3 w-3 text-amber-900" aria-hidden="true" />
              </span>
            </span>
          )}
          <button
            onClick={onRecoverClick}
            disabled={recoveryLoading}
            title={`${recoveryTitle}${recoveryDescription ? ': ' + recoveryDescription : ''}`}
            aria-label={`${recoveryActionLabel} — ${recoveryTitle}${recoveryDescription ? ': ' + recoveryDescription : ''}`}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-amber-400/20 bg-amber-400/8 text-amber-200/90 transition-all hover:border-amber-300/40 hover:bg-amber-400/15 hover:text-amber-100 disabled:cursor-wait disabled:opacity-50"
          >
            <Icon icon={recoveryLoading ? 'bx:refresh' : 'solar:restart-bold'} className={`h-4 w-4 ${recoveryLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
          </button>
        </div>
      )}
      {reportDownloadUrl && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            window.open(reportDownloadUrl, '_blank', 'noopener,noreferrer');
          }}
          className="mt-3 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-gray-300 transition-all hover:border-[#15FBFF]/35 hover:bg-[#15FBFF]/10 hover:text-[#BFFFFF]"
          title="Download lead report"
          aria-label={`Download lead report for ${name}`}
        >
          <Icon icon="solar:download-linear" className="h-4.5 w-4.5" aria-hidden="true" />
        </button>
      )}
      {onOpenISR && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenISR(event);
          }}
          className="mt-3 inline-flex h-9 items-center justify-center gap-2 rounded-xl border border-cyan-300/25 bg-cyan-300/[0.08] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-cyan-100 transition-colors hover:border-cyan-200/45 hover:bg-cyan-300/[0.14] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200/70"
          aria-label={`Open SDR workspace for ${name}`}
        >
          <Icon icon="bx:headphone" className="h-4 w-4" aria-hidden="true" />
          SDR
        </button>
      )}
      </div>
  );
};
